import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Building, Search, Sparkles } from 'lucide-react';
import { api, type CustomersView } from '../api';
import { AskAbout } from '../amy/ask-about';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { useAgentName } from '../agent-name';
import { Pager } from '../components/pager';
import { EmptyNote, MoneyLines, StatusBadges, ago, formatDay, useDeskData } from '../components/desk';
import '../customer-360.css';

type Row = CustomersView['rows'][number];
type SortKey = 'name' | 'risk' | 'habit' | 'balance' | 'overdue' | 'oldest' | 'contact' | 'next';
type View = keyof CustomersView['views'];
const columns: { key: SortKey; label: string; numeric?: boolean; descending?: boolean }[] = [
  { key: 'name', label: 'Customer' }, { key: 'risk', label: 'Attention', descending: true },
  { key: 'next', label: 'Where it stands' }, { key: 'balance', label: 'Open balance', numeric: true },
  { key: 'overdue', label: 'Overdue', numeric: true }, { key: 'oldest', label: 'Oldest overdue', numeric: true },
  { key: 'contact', label: 'Last contact' }, { key: 'habit', label: 'Payment habit', descending: true },
];
const views: { key: View; label: string }[] = [
  { key: 'needs_person', label: 'Needs a person' }, { key: 'promise_week', label: 'Promise due this week' },
  { key: 'quiet_14', label: 'No contact in 14 days' }, { key: 'disputed', label: 'Disputed' }, { key: 'high_balance', label: 'High balance' },
];
const viewKeys = new Set<string>(views.map(v => v.key));
const sortKeys = new Set<SortKey>(columns.map(column => column.key));
const defaultDescending = (key: SortKey) => key === 'risk' || key === 'habit' || key === 'balance' || key === 'overdue' || key === 'oldest';
const pageSize = 50;
interface Params { page: number; sort: SortKey; desc: boolean; q: string; view: View | null }

function parseParams(hash: string): Params {
  const query = hash.replace(/^#/, '').split('?')[1] ?? '';
  const params = new URLSearchParams(query);
  const requested = Number(params.get('page'));
  const sort = params.get('sort') as SortKey | null;
  const key = sort && sortKeys.has(sort) ? sort : 'risk';
  const desc = params.get('desc');
  return { page: Number.isInteger(requested) && requested > 0 ? requested : 1, sort: key,
    desc: desc === null ? defaultDescending(key) : desc === '1' || desc === 'true', q: (params.get('q') ?? '').slice(0, 200),
    view: viewKeys.has(params.get('view') ?? '') ? params.get('view') as View : null };
}
function serializeParams(params: Params) {
  const query = new URLSearchParams({ sort: params.sort, desc: params.desc ? '1' : '0' });
  if (params.page > 1) query.set('page', String(params.page));
  if (params.q) query.set('q', params.q);
  if (params.view) query.set('view', params.view);
  return `#customers?${query}`;
}
const riskLabels: Record<string, string> = { high: 'High', medium: 'Medium', low: 'Low', none: 'None' };
const when = (date: string, asOf: string) => date.slice(0, 10) === asOf.slice(0, 10) ? 'today' : formatDay(date.slice(0, 10), asOf);
/** Amy's state in words: what is open with them, else whether she is waiting on them, else her plan. */
function stateText(row: Row, asOf: string): { text: string; tone: 'you' | 'them' | 'amy' | 'done' } {
  const { kind, on } = row.amyState;
  switch (kind) {
    case 'dispute': return { text: 'Dispute open', tone: 'you' };
    case 'draft': return { text: 'Draft waiting for you', tone: 'you' };
    case 'replied': return { text: `They replied${on ? ` ${when(on, asOf)}` : ''}; your review`, tone: 'you' };
    case 'broken': return { text: `Broke a promise${on ? ` (${formatDay(on, asOf)})` : ''}`, tone: 'you' };
    case 'promise': return { text: `Promise ${on ? formatDay(on, asOf) : 'open'}`, tone: 'them' };
    case 'paused': return { text: 'Reminders paused', tone: 'amy' };
    case 'waiting': return { text: `Waiting on reply since ${on ? formatDay(on.slice(0, 10), asOf) : 'the last email'}`, tone: 'them' };
    case 'settled': return { text: 'Paid up', tone: 'done' };
    default: return { text: stepText(row, asOf), tone: 'amy' };
  }
}
const reliability = (row: Row) => {
  const { kept, broken } = row.promiseRecord;
  return kept + broken >= 2 ? `Kept ${kept} of ${kept + broken} promises` : null;
};

/**
 * Amy's one-line read from the customer's file, fetched when a person hovers the name. One request per customer per
 * minute (at most 200 kept), shared by every hover; a failed read is dropped so the next hover asks again.
 */
const summaries = new Map<string, { at: number; read: Promise<string | null> }>();
const summaryTtl = 60_000; const summaryCap = 200;
// Any change on the server (a note saved, the file rewritten) may change a summary, so the next hover reads it fresh.
if (typeof window !== 'undefined') window.addEventListener('alder:state-changed', () => summaries.clear());
function readSummary(id: string) {
  const hit = summaries.get(id);
  if (hit && Date.now() - hit.at < summaryTtl) return hit.read;
  for (const [key, entry] of summaries) if (Date.now() - entry.at >= summaryTtl) summaries.delete(key);
  while (summaries.size >= summaryCap) summaries.delete(summaries.keys().next().value!);
  const read = api<{ memory: { summary: string } | null }>(`/app-api/customers/${encodeURIComponent(id)}/memory`)
    .then(r => r.memory?.summary?.trim() || null);
  read.catch(() => { if (summaries.get(id)?.read === read) summaries.delete(id); });
  summaries.set(id, { at: Date.now(), read });
  return read;
}
function FileSummary({ id }: { id: string }) {
  const agent = useAgentName();
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'read'; summary: string | null } | { kind: 'failed' }>({ kind: 'loading' });
  useEffect(() => {
    let live = true; setState({ kind: 'loading' });
    readSummary(id).then(summary => { if (live) setState({ kind: 'read', summary }); }, () => { if (live) setState({ kind: 'failed' }); });
    return () => { live = false; };
  }, [id]);
  const text = state.kind === 'loading' ? 'Reading the file…' : state.kind === 'failed' ? 'The file could not be read just now. Hover again to retry.'
    : state.summary ?? `${agent} has nothing on file yet.`;
  return <div className="customer-hover"><strong><Sparkles size={13} aria-hidden />What {agent} knows</strong>
    <p className={state.kind === 'read' && state.summary ? '' : 'muted'} role={state.kind === 'failed' ? 'alert' : undefined}>{text}</p></div>;
}

const stepText = (row: Row, asOf: string) => row.nextStep.status === 'awaiting_approval' ? 'Awaiting approval'
  : row.nextStep.status === 'paused' ? 'Paused' : row.nextStep.date ? `${row.nextStep.title}, ${row.nextStep.date === asOf ? 'today'
    : formatDay(row.nextStep.date, asOf)}` : row.nextStep.title;

export function Customers() {
  const [params, setParams] = useState<Params>(() => parseParams(window.location.hash));
  const [query, setQuery] = useState(params.q);
  useEffect(() => {
    const change = () => setParams(parseParams(window.location.hash));
    window.addEventListener('hashchange', change);
    return () => window.removeEventListener('hashchange', change);
  }, []);
  useEffect(() => setQuery(params.q), [params.q]);
  useEffect(() => {
    const next = query.trim();
    if (next === params.q) return;
    const timer = window.setTimeout(() => {
      const merged = { ...params, q: next, page: 1 };
      setParams(merged); window.location.hash = serializeParams(merged);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query, params]);
  const apply = (next: Partial<Params>) => {
    const merged = { ...params, ...next }; setParams(merged); window.location.hash = serializeParams(merged);
  };
  const path = useMemo(() => {
    const search = new URLSearchParams({ page: String(params.page), pageSize: String(pageSize), sort: params.sort,
      desc: params.desc ? '1' : '0' });
    if (params.q) search.set('q', params.q);
    if (params.view) search.set('view', params.view);
    return `/app-api/customers?${search}`;
  }, [params]);
  const { data, error } = useDeskData<CustomersView>(path);
  const open = (id: string) => { window.location.hash = `customers/${encodeURIComponent(id)}`; };
  const start = data && data.total ? (data.page - 1) * data.pageSize + 1 : 0;
  const end = data ? Math.min(data.page * data.pageSize, data.total) : 0;
  return <main id="main-content" className="queue-page desk-page">
    <header className="page-heading"><div><h1>Customers<span className="heading-dot" aria-hidden="true">.</span></h1>
      <p>{data ? `${data.total} ${data.total === 1 ? 'customer' : 'customers'}, ${data.withBalance} with an open balance.` : 'Reading the ledger…'}
        {data && data.rows.length > 0 && data.rows.every(r => r.behavior.kind === 'unknown')
          && ' Payment habits appear once customers have paid invoices on record.'}</p></div>
      <label className="search-field desk-search"><Search size={14} aria-hidden /><span className="sr-only">Search customers</span>
        <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name" /></label>
    </header>
    <nav className="customer-views" aria-label="Saved views">
      <button type="button" aria-pressed={!params.view} onClick={() => apply({ view: null, page: 1 })}>All customers</button>
      {views.map(v => <button key={v.key} type="button" aria-pressed={params.view === v.key}
        onClick={() => apply({ view: params.view === v.key ? null : v.key, page: 1 })}>
        {v.label}{data && <span>{data.views[v.key]}</span>}</button>)}
    </nav>
    {error && <div className="alert error" role="alert">{error}</div>}
    {data && (data.total ? <><div className="desk-table-wrap"><table className="desk-table customers-table">
      <thead><tr>{columns.map(c => <th key={c.key} className={c.numeric ? 'numeric' : ''}
        aria-sort={params.sort === c.key ? (params.desc ? 'descending' : 'ascending') : 'none'}>
        <button type="button" onClick={() => apply({ sort: c.key,
          desc: params.sort === c.key ? !params.desc : defaultDescending(c.key), page: 1 })}>
          {c.label}{params.sort === c.key && (params.desc ? <ArrowDown size={12} aria-hidden /> : <ArrowUp size={12} aria-hidden />)}</button>
      </th>)}</tr></thead>
      <tbody>{data.rows.map(c => <tr key={c.id} onClick={() => open(c.id)}>
        <td><HoverCard openDelay={350} closeDelay={80}><HoverCardTrigger asChild>
          <a href={`#customers/${encodeURIComponent(c.id)}`} onClick={e => e.stopPropagation()}>{c.name}</a></HoverCardTrigger>
          <HoverCardContent align="start" className="customer-hover-card"><FileSummary id={c.id} /></HoverCardContent></HoverCard><AskAbout customerId={c.id} name={c.name} />
          <StatusBadges badges={c.badges} status={c.status} /></td>
        <td><span className={`risk ${c.risk.level}`}><i aria-hidden />{riskLabels[c.risk.level]}</span></td>
        <td className="customer-state">{(() => { const st = stateText(c, data.asOf); return <>
          <span className={`state ${st.tone}`}>{st.text}</span>
          {!['settled', 'planned'].includes(c.amyState.kind) && c.nextStep.status !== 'paused' && <small className={`next-cell ${c.nextStep.status}`}>Next: {stepText(c, data.asOf)}</small>}</>; })()}</td>
        <td className="numeric"><MoneyLines values={c.balances} empty="Settled" /></td>
        <td className="numeric"><MoneyLines values={c.overdue} /></td>
        <td className="numeric">{c.oldestDaysOverdue ? `${c.oldestDaysOverdue} days` : <span className="muted">–</span>}</td>
        <td>{c.lastContact ? <>{ago(c.lastContact.at, data.asOf)}<small className="muted">
          {c.lastContact.direction === 'in' ? 'They replied' : 'We emailed'}</small></> : <span className="muted" title="No emails yet">–</span>}</td>
        <td>{c.behavior.kind === 'unknown' ? <span className="muted" title={c.behavior.label}>–</span>
          : <span className={`habit ${c.behavior.kind}`}>{c.behavior.label}</span>}
          {reliability(c) && <small className="muted">{reliability(c)}</small>}</td>
      </tr>)}</tbody>
    </table></div>
    <footer className="desk-pager-footer">
      <span>Showing {start}–{end} of {data.total}</span>
      <Pager page={data.page} pages={data.pages} label="Customer pages" onPage={page => apply({ page })} />
    </footer></>
      : params.view && !params.q ? <EmptyNote icon={<Building size={22} aria-hidden />} title={`Nobody in “${views.find(v => v.key === params.view)!.label}”`}>
        That is good news. Choose All customers to see the rest.</EmptyNote>
      : params.q ? <EmptyNote icon={<Search size={22} aria-hidden />} title={`No customer matches “${params.q}”`}>
        Search by the name in your ledger.</EmptyNote>
        : <EmptyNote icon={<Building size={26} aria-hidden />} title="No customers yet">
          Customers appear after the first ledger sync.</EmptyNote>)}
  </main>;
}
